import { resolveProtocolRelative } from '../getLinkType';
import {
  APPLY_V3_URL_PREFIX,
  PAGE_TYPE_MAPPINGS,
  VERTICAL_MAPPING_BY_BRAND,
} from '../routing';

/**
 * Raw TI search query-param names carried in the URL by the TravelPolicySearch
 * component. The canonical writer is `STATE_SCHEMA` in fe-services
 * `src/components/cms/components-user/travel/TravelPolicySearch/useTravelSearchQuery.ts`,
 * which is compile-time-coupled to this list via
 * `satisfies Record<TiSearchParamKey, ...>` — adding a param there without
 * registering it here fails the fe-services build. These params must stay
 * inside the TI purchase funnel (CMS-1866) — they are stripped from links
 * leading outside of it.
 */
const TRAVEL_SEARCH_URL_PARAM_KEYS_LIST = [
  'destinations',
  'tripType',
  'region',
  'startDate',
  'endDate',
  'noOfAdults',
  'noOfChildren',
  'travelAsFamily',
] as const;

export type TiSearchParamKey =
  (typeof TRAVEL_SEARCH_URL_PARAM_KEYS_LIST)[number];

export const TRAVEL_SEARCH_URL_PARAM_KEYS: ReadonlySet<string> = new Set(
  TRAVEL_SEARCH_URL_PARAM_KEYS_LIST,
);

/** All URL fragments that map to the TRAVEL_INSURANCE vertical on any brand. */
const TI_VERTICAL_FRAGMENTS = new Set(
  Object.values(VERTICAL_MAPPING_BY_BRAND).flatMap((mapping) =>
    Object.keys(mapping).filter((key) => mapping[key] === 'TRAVEL_INSURANCE'),
  ),
);

/**
 * Editorial subtrees of the TI vertical that are NOT part of the purchase
 * funnel: blogs and author pages strip TI params like any other non-funnel
 * page. Derived from `PAGE_TYPE_MAPPINGS` so a fragment rename cannot
 * silently reopen the leak.
 */
const TI_EDITORIAL_SUBTREES = new Set(
  [PAGE_TYPE_MAPPINGS.article, PAGE_TYPE_MAPPINGS.author].filter(
    (fragment): fragment is string => Boolean(fragment),
  ),
);

/**
 * The deeplinked click-out chain (click-out → Reward Form → Provider Page)
 * must keep forwarding params unchanged — the Reward Form re-sends
 * `location.search` to `/apply/[productId]/redirect`, which splices it into
 * the external provider deeplink.
 */
const APPLY_FLOW_ROOTS = new Set([
  'apply',
  'apply-v2a',
  'apply-v2b',
  APPLY_V3_URL_PREFIX,
]);

const LANG_PREFIX_REGEX = /^[a-z]{2}(-[a-z]{2})?$/i;

const getPathname = (link: string): string => {
  const absolute = resolveProtocolRelative(link);
  if (absolute.startsWith('http://') || absolute.startsWith('https://')) {
    try {
      return new URL(absolute).pathname;
    } catch {
      return '';
    }
  }
  return link.split('#')[0].split('?')[0];
};

/**
 * Whether an internal link points inside the TI purchase funnel (CMS-1866):
 * - any page of the TI vertical except its editorial subtrees (Result Page,
 *   Provider Page, E2E checkout `/travel-insurance/checkout/...`, TI landing
 *   pages that may host the travel search component);
 * - the apply flow (deeplinked click-out chain through the Reward Form).
 *
 * TI search params are preserved on navigation within the funnel and stripped
 * from links leading anywhere else.
 */
export const isTiPurchaseFunnelPath = (link: string): boolean => {
  const segments = getPathname(link).split('/').filter(Boolean);
  if (segments.length > 1 && LANG_PREFIX_REGEX.test(segments[0])) {
    segments.shift();
  }
  const [root, second] = segments;
  if (root !== undefined && TI_VERTICAL_FRAGMENTS.has(root)) {
    return second === undefined || !TI_EDITORIAL_SUBTREES.has(second);
  }
  return root !== undefined && APPLY_FLOW_ROOTS.has(root);
};
